import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { addProject, removeProject } from '../../../utils/demoEnvMgmtApi';
import { errorMessage } from './error-message';
import { formatProjectLabel } from '../label-format';
import ConfirmDeleteDialog from '../../../components/admin/ConfirmDeleteDialog';

function ProjectsSection({ projects, accounts, onChanged }) {
  const [projectId, setProjectId] = useState('');
  const [label, setLabel] = useState('');
  const [accountId, setAccountId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const hasAccounts = accounts.length > 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!projectId.trim() || !label.trim()) return;
    if (!accountId) {
      setError('Select an account for this project — add one above if none exist yet.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addProject({ projectId: Number(projectId), label: label.trim(), demoEnvAccountId: Number(accountId) });
      setProjectId('');
      setLabel('');
      setAccountId('');
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await removeProject(deleteTarget.id);
      setDeleteTarget(null);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setDeleteTarget(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-env-mgmt-section">
      <h2 className="demo-env-mgmt-section-title">[L2] Projects</h2>
      <p className="demo-env-mgmt-label">
        CSQ XP master projects used for project-level entities (charts/dashboards) and as the scope
        that target datasources are linked to (a project can have many target datasources).
      </p>

      <div className="demo-env-mgmt-columns" style={{ marginTop: 8 }}>
        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Add project</h3>
          {!hasAccounts && (
            <p className="demo-env-mgmt-label" style={{ marginBottom: 6 }}>
              Add an account above before adding a project — every project must be linked to one.
            </p>
          )}
          <form onSubmit={handleAdd}>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label" htmlFor="new-project-id">Project id</label>
              <input
                id="new-project-id"
                className="demo-env-mgmt-input"
                type="number"
                min="1"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
              <label className="demo-env-mgmt-label" htmlFor="new-project-label">Label</label>
              <input
                id="new-project-label"
                className="demo-env-mgmt-input"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
              <label className="demo-env-mgmt-label" htmlFor="new-project-account">Account</label>
              <select
                id="new-project-account"
                className="demo-env-mgmt-select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                required
              >
                <option value="">{hasAccounts ? 'Select an account…' : 'No accounts yet'}</option>
                {accounts.map((account) => (
                  <option key={account.id} value={String(account.id)}>{account.label}</option>
                ))}
              </select>
            </div>
            <div className="demo-env-mgmt-field demo-env-mgmt-field-actions" style={{ marginTop: 8 }}>
              <button className="demo-env-mgmt-btn" type="submit" disabled={submitting || !hasAccounts}>
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
          {error && <p className="demo-env-mgmt-error">{error}</p>}
        </div>

        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">[L2] Projects</h3>
          <div className="demo-env-mgmt-table-wrap">
            <table className="demo-env-mgmt-table">
              <thead>
                <tr>
                  <th>Project id</th>
                  <th>Label</th>
                  <th>Account</th>
                  <th className="demo-env-mgmt-table-actions" />
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <a href={`https://app.contentsquare.com/#/home?project=${project.projectId}`} target="_blank" rel="noopener noreferrer">
                        {project.projectId}
                      </a>
                    </td>
                    <td>{project.label}</td>
                    <td>{project.account ? project.account.label : '—'}</td>
                    <td className="demo-env-mgmt-table-actions">
                      <button
                        type="button"
                        className="demo-env-mgmt-btn-delete"
                        onClick={() => setDeleteTarget(project)}
                        disabled={submitting}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {projects.length === 0 && (
                  <tr className="demo-env-mgmt-empty-row">
                    <td colSpan={4}>No projects yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDeleteDialog
          entityType="project"
          entityName={formatProjectLabel(deleteTarget)}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default ProjectsSection;
